import { z } from 'zod';
import {
    DateTimeUtils,
    getDefaults,
    lt,
    MAX_INPUT_TEXT_LENGTH,
    useDateTimeLocalizationInfo,
    useTimezone,
    Validation,
} from '@mews/framework';
import { Counter, EnterpriseScopeCounterDiscriminator } from '@mews-commander/data-access';
import { compactArray } from '@mews/fp';
import { SchemaFormDefinition } from '@mews-commander/form';
import { Stack, StackItem } from '@optimus-web/core';
import { LocalizedTypography } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { CounterFormField, CounterUseFor } from '../../CounterForm/formMapping';
import { CreditNoteCounterPreviewBanner } from './CreditNoteCounterPreviewBanner';
import { DefaultCreditNoteWarningBanner } from './DefaultCreditNoteWarningBanner';

export const creditNoteCounterValidationSchema = z.object({
    title: Validation.String.Required.max(MAX_INPUT_TEXT_LENGTH).default(''),
    name: Validation.String.Required.max(MAX_INPUT_TEXT_LENGTH).default(''),
    startValue: Validation.Number.MaxPositiveInteger.default(1),
    numberOfDigits: Validation.Number.MaxPositiveInteger.default(1),
    prefix: Validation.String.Empty.nullish().default(''),
    displayCID: z.boolean().default(false),
    billTypeCode: Validation.String.Empty.nullish().default(''),
    isDefault: z.boolean().default(false),
    useFor: z.array(z.nativeEnum(CounterUseFor)).min(1).default([]),
    created: z.string().optional(),
    updated: z.string().optional(),
});

export const getCreditNoteCounterFormDefinition = (isEditForm: boolean, defaultValues: CreditNoteFormValues) =>
    ({
        fields: {
            title: {
                label: lt('CreditNoteCounterTitleLabel'),
                placeholder: lt('CreditNoteCounterTitlePlaceholder'),
                helper: lt('CreditNoteCounterTitleHelper'),
            },
            isDefault: {
                label: lt('MakeDefault'),
                field: {
                    type: 'switch',
                },
            },
            useFor: {
                label: lt('CreditNoteCounterUseForLabel'),
                field: {
                    type: 'checkboxGroup',
                    options: [
                        {
                            name: CounterFormField.UseFor,
                            value: CounterUseFor.bills,
                            label: lt('BillPlural'),
                        },
                        {
                            name: CounterFormField.UseFor,
                            value: CounterUseFor.invoices,
                            label: lt('InvoicePlural'),
                        },
                    ],
                },
            },
            name: {
                label: lt('CounterName'),
                placeholder: lt('CounterNamePlaceholder'),
                helper: lt('CounterNameHelper'),
            },
            startValue: {
                required: true,
                helper: lt('StartValueHelper'),
                label: lt('StartValue'),
                placeholder: lt('StartValuePlaceholder'),
            },
            prefix: {
                label: lt('CreditNoteCounterPrefixTitle'),
                placeholder: lt('CreditNoteCounterPrefixPlaceholder'),
                helper: lt('CreditNoteCounterPrefixHelper'),
            },
            numberOfDigits: {
                helper: lt('CreditNoteCounterNumberOfDigitsHelper'),
                label: lt('CreditNoteCounterNumberOfDigitsLabel'),
                field: {
                    type: 'counter',
                    min: 1,
                },
            },
            billTypeCode: {
                label: lt('BillTypeCode'),
                helper: lt('BillTypeCodeHelper'),
                placeholder: lt('BillTypeCodePlaceholder'),
            },
            displayCID: {
                label: lt('DisplayCIDOnBillsAndInvoices'),
            },
            created: {
                label: lt('Created'),
                readOnly: true,
            },
            updated: {
                label: lt('Updated'),
                readOnly: true,
            },
        },
        renderFields: (fields) => (
            <Stack vertical spacing="200">
                <LocalizedTypography textKey={TextKey.Settings} textStyle="titleSmallStrong" as="div" />
                {fields.title}
                {fields.isDefault}
                <DefaultCreditNoteWarningBanner defaultValues={defaultValues} isEditForm={isEditForm} />
                {fields.useFor}
                {fields.name}
                <LocalizedTypography
                    textKey={TextKey.CreditNoteCounterNumberFormattingSectionTitle}
                    textStyle="titleSmallStrong"
                    as="div"
                />
                {!isEditForm ? fields.startValue : null}
                <Stack spacing="200">
                    <StackItem grow>{fields.prefix}</StackItem>
                    <StackItem grow>{fields.numberOfDigits}</StackItem>
                </Stack>
                <CreditNoteCounterPreviewBanner />
                {fields.billTypeCode}
                {fields.displayCID}
                {isEditForm ? fields.created : null}
                {isEditForm ? fields.updated : null}
            </Stack>
        ),
    }) satisfies SchemaFormDefinition<typeof creditNoteCounterValidationSchema>;

export const useDefaultValues = (counter: Counter | undefined) => {
    const timezone = useTimezone();
    const localizationInfo = useDateTimeLocalizationInfo();

    if (counter && counter.data.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter) {
        return {
            ...creditNoteCounterDefaultValues,
            title: counter.data.value.title,
            name: counter.name,
            startValue: counter.value,
            prefix: counter.data.value.prefix || null,
            displayCID: counter.data.value.displayCid,
            billTypeCode: counter.data.value.billTypeCode,
            isDefault: counter.isDefault,
            numberOfDigits: counter.data.value.numberOfDigits,
            useFor: compactArray([
                counter.data.value.applicableBillType.applyToInvoice ? CounterUseFor.invoices : null,
                counter.data.value.applicableBillType.applyToBill ? CounterUseFor.bills : null,
            ]),
            created: counter?.createdUtc
                ? DateTimeUtils.dateTimeUtcStringToDateTimeString(counter.createdUtc, localizationInfo, timezone)
                : undefined,
            updated: counter?.updatedUtc
                ? DateTimeUtils.dateTimeUtcStringToDateTimeString(counter.updatedUtc, localizationInfo, timezone)
                : undefined,
        } satisfies CreditNoteFormValues;
    }
    return creditNoteCounterDefaultValues;
};

export const creditNoteCounterDefaultValues = getDefaults(creditNoteCounterValidationSchema) as CreditNoteFormValues;
export type CreditNoteFormValues = z.infer<typeof creditNoteCounterValidationSchema>;
